import type { CrowdexAgentReminder, CrowdexSavedAgent } from '@crowdex/agent-contracts';
import { ArrowLeft, Bot, Settings2 } from 'lucide-react';

const reminderLabels: Record<CrowdexAgentReminder['trigger'], string> = {
  'before-turn': 'Before every turn',
  'after-handoff': 'After a change of agent or environment',
  'workspace-changed': 'After a change of environment, repository or branch',
};

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-hairline bg-card px-3 py-2.5">
      <dt className="text-caption font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 break-words text-sm text-foreground">{value}</dd>
    </div>
  );
}

function EmptyValue({ children }: { children: React.ReactNode }) {
  return <p className="text-sm italic text-muted-foreground">{children}</p>;
}

/** An action on a profile: customize a system profile, reset an override. */
export type AgentProfileAction = { id: string; label: string; run(): void };

/** An agent profile, system or the account's (`/api/agent-profiles`), read-only. */
export function AgentProfileViewer({
  profile,
  actions = [],
  onBack,
  backLabel = 'Back to agents',
  onConfigure,
}: {
  profile: CrowdexSavedAgent;
  actions?: readonly AgentProfileAction[];
  onBack: () => void;
  /** Where back goes, as its button says it. */
  backLabel?: string;
  onConfigure?: () => void;
}) {
  const capabilityMode = profile.capabilityMode ?? 'inherit';
  const permissionMode = profile.policy?.permissionMode ?? 'allow-all';
  const reminders = [...(profile.systemReminders ?? [])].sort(
    (left, right) => right.priority - left.priority,
  );

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <header className="flex shrink-0 items-center gap-3 border-hairline border-b px-4 py-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 rounded-md px-2 py-1.5 text-muted-foreground text-xs hover:bg-accent hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" /> {backLabel}
        </button>
        <div className="ml-auto flex items-center gap-2">
          {actions.map((action) => (
            <button
              type="button"
              key={action.id}
              onClick={action.run}
              className="rounded-lg border border-hairline px-3 py-2 font-medium text-xs hover:bg-accent"
            >
              {action.label}
            </button>
          ))}
          {onConfigure && (
            <button
              type="button"
              onClick={onConfigure}
              className="flex items-center gap-2 rounded-lg bg-primary px-3 py-2 font-medium text-primary-foreground text-xs"
            >
              <Settings2 className="size-3.5" /> Configure
            </button>
          )}
        </div>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <article className="mx-auto max-w-4xl space-y-8 px-5 py-8 sm:px-8">
          <div className="flex items-start gap-4">
            <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-desktop-agent/10 text-desktop-agent">
              <Bot className="size-6" />
            </span>
            <div className="min-w-0">
              <p className="text-caption font-medium uppercase tracking-wide text-muted-foreground">
                Agent profile
              </p>
              <h1 className="type-section-title mt-1">{profile.name}</h1>
              {profile.description && (
                <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
                  {profile.description}
                </p>
              )}
            </div>
          </div>

          <section aria-labelledby="agent-runtime-heading">
            <h2 id="agent-runtime-heading" className="mb-3 text-sm font-semibold">
              Runtime and policy
            </h2>
            <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Detail label="Runtime" value={profile.agentId} />
              <Detail
                label="Model"
                value={
                  profile.modelMode === 'fixed' && profile.modelId
                    ? profile.modelId
                    : 'Inherited from conversation'
                }
              />
              <Detail label="Workspace access" value={permissionMode.replaceAll('-', ' ')} />
              <Detail label="Collaboration" value={profile.collaboration?.strategy ?? 'solo'} />
            </dl>
          </section>

          <section aria-labelledby="agent-instructions-heading">
            <h2 id="agent-instructions-heading" className="mb-3 text-sm font-semibold">
              Instructions
            </h2>
            {profile.instructions?.trim() ? (
              <pre className="whitespace-pre-wrap rounded-xl border border-hairline bg-card p-4 font-sans text-sm leading-6">
                {profile.instructions}
              </pre>
            ) : (
              <EmptyValue>No custom instructions.</EmptyValue>
            )}
          </section>

          <section aria-labelledby="agent-reminders-heading">
            <h2 id="agent-reminders-heading" className="mb-3 text-sm font-semibold">
              System reminders
            </h2>
            {reminders.length ? (
              <div className="space-y-3">
                {reminders.map((reminder) => (
                  <div className="rounded-xl border border-hairline bg-card p-4" key={reminder.id}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-medium">
                        {reminderLabels[reminder.trigger] ?? reminder.trigger}
                      </p>
                      <span className="rounded-sm bg-muted px-2 py-0.5 text-caption text-muted-foreground">
                        Priority {reminder.priority}
                      </span>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-foreground/85">
                      {reminder.content}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyValue>No system reminders.</EmptyValue>
            )}
          </section>

          <section aria-labelledby="agent-capabilities-heading">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 id="agent-capabilities-heading" className="text-sm font-semibold">
                Capabilities
              </h2>
              <span className="rounded-sm bg-muted px-2 py-1 text-caption capitalize text-muted-foreground">
                {capabilityMode}
              </span>
            </div>
            <h3 className="mb-3 text-xs font-semibold">Optional MCP servers and skills</h3>
            {capabilityMode === 'inherit' ? (
              <EmptyValue>
                Inherited from the conversation. The effective capabilities depend on its active
                plugins and runtime.
              </EmptyValue>
            ) : capabilityMode === 'none' ? (
              <EmptyValue>No optional MCP servers or skills.</EmptyValue>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-hairline bg-card p-4">
                  <h3 className="text-xs font-semibold">MCP servers</h3>
                  {profile.mcpServerNames?.length ? (
                    <ul className="mt-3 space-y-2 text-sm">
                      {profile.mcpServerNames.map((name) => (
                        <li className="break-all font-mono text-xs" key={name}>
                          {name}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <EmptyValue>None selected.</EmptyValue>
                  )}
                </div>
                <div className="rounded-xl border border-hairline bg-card p-4">
                  <h3 className="text-xs font-semibold">Skills</h3>
                  {profile.skillRefs?.length ? (
                    <ul className="mt-3 space-y-2 text-sm">
                      {profile.skillRefs.map((skill) => (
                        <li key={`${skill.plugin}:${skill.uri}`}>
                          <p>{skill.name}</p>
                          <p className="break-all text-caption text-muted-foreground">
                            {skill.plugin} · {skill.uri}
                          </p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <EmptyValue>None selected.</EmptyValue>
                  )}
                </div>
              </div>
            )}
          </section>

          <section aria-labelledby="agent-experience-heading">
            <h2 id="agent-experience-heading" className="mb-3 text-sm font-semibold">
              Delegation
            </h2>
            <dl className="grid gap-3 sm:grid-cols-2">
              <Detail
                label="Handoff targets"
                value={profile.collaboration?.handoffTargets?.join(', ') || 'None'}
              />
            </dl>
          </section>
        </article>
      </div>
    </div>
  );
}
